import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { Body, MetricValue, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { useThemeColors } from "#hooks/use-theme-colors"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import { TrendingDown, TrendingUp } from "lucide-react"
import React, { useMemo } from "react"
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts"

interface MonthlyExpensesCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const MonthlyExpensesCard = React.memo<MonthlyExpensesCardProps>(({ analytics }) => {
  const themeColors = useThemeColors()
  const { resolvedTheme } = useTheme()
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const isDark = resolvedTheme === "dark"

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = React.useMemo(() => {
    if (!analytics?.totalsByMonth || analytics.totalsByMonth.length === 0) return -1
    return analytics.totalsByMonth.length - 1
  }, [analytics])

  const selectedMonthTotals =
    selectedMonthIndex >= 0 ? analytics?.totalsByMonth?.[selectedMonthIndex] : undefined
  const totalExpenses = selectedMonthTotals?.totalExpenses ?? 0
  const previousMonthTotals = analytics?.totalsByMonth?.[selectedMonthIndex - 1]
  const previousExpenses = previousMonthTotals?.totalExpenses ?? 0
  const changePercent =
    previousExpenses > 0 ? ((totalExpenses - previousExpenses) / previousExpenses) * 100 : undefined
  const isIncreasing = changePercent !== undefined && changePercent > 0

  // Get daily expenses for current and previous month
  const dailyExpensesByMonth = analytics?.dailyExpensesByMonth || []
  const currentMonthDaily = dailyExpensesByMonth.find(
    (m: { month: string }) => m.month === selectedMonthTotals?.month
  )
  const previousMonthDaily = dailyExpensesByMonth.find(
    (m: { month: string }) => m.month === previousMonthTotals?.month
  )

  // Merge daily data for both months, aligned by day number (cumulative)
  const chartData = useMemo(() => {
    if (!currentMonthDaily) return []

    const maxDays = Math.max(
      currentMonthDaily.dailyExpenses.length,
      previousMonthDaily?.dailyExpenses.length || 0
    )

    return Array.from({ length: maxDays }, (_, index) => {
      const day = index + 1
      const currentDayData = currentMonthDaily.dailyExpenses.find(
        (d: { day: number }) => d.day === day
      )
      const previousDayData = previousMonthDaily?.dailyExpenses.find(
        (d: { day: number }) => d.day === day
      )

      return {
        day,
        current: currentDayData?.cumulativeAmount,
        previous: previousDayData?.cumulativeAmount,
      }
    })
  }, [currentMonthDaily, previousMonthDaily])

  // Daily (non-cumulative) data for both months
  const dailyChartData = useMemo(() => {
    if (!currentMonthDaily) return []

    const maxDays = Math.max(
      currentMonthDaily.dailyExpenses.length,
      previousMonthDaily?.dailyExpenses.length || 0
    )

    return Array.from({ length: maxDays }, (_, index) => {
      const day = index + 1
      const currentDayData = currentMonthDaily.dailyExpenses.find(
        (d: { day: number }) => d.day === day
      )
      const previousDayData = previousMonthDaily?.dailyExpenses.find(
        (d: { day: number }) => d.day === day
      )

      return {
        day,
        current: currentDayData?.dailyAmount,
        previous: previousDayData?.dailyAmount,
      }
    })
  }, [currentMonthDaily, previousMonthDaily])

  const currentColor = themeColors.primary
  const previousColor = themeColors.popoverForeground

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isDark
              ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
              : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0">
          <div className="flex items-start justify-between mb-4 flex-shrink-0">
            <div className="flex-1">
              <CardTitle className="text-base font-medium mb-3 text-foreground">
                {t("components.dashboard.monthlyExpenses.title")}
              </CardTitle>
              <motion.div variants={statValue}>
                <StatValue className="text-3xl sm:text-4xl mb-2">
                  {formatCurrency(totalExpenses)}
                </StatValue>
              </motion.div>
              {previousMonthTotals && (
                <Body className="text-xs text-muted-foreground mb-2">
                  {previousMonthTotals.monthLabel}:{" "}
                  <PrivacyBlur>{formatCurrency(previousExpenses)}</PrivacyBlur>
                </Body>
              )}

              {changePercent !== undefined && (
                <div className="flex items-center gap-1.5 text-sm">
                  <span
                    className={`flex items-center gap-1.5 text-sm ${isIncreasing ? "text-destructive font-medium" : "text-green-600 font-medium"}`}
                  >
                    {isIncreasing ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}{" "}
                    {isIncreasing ? "+" : ""}
                    {changePercent.toFixed(1)}%
                  </span>
                  <span className="text-muted-foreground">
                    {t("components.dashboard.monthlyExpenses.fromLastMonth")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tiny Line Chart for Expenses - Cumulative */}
          <div className="mb-4">
            <Body className="text-sm font-medium text-foreground mb-1.5">
              {t("components.dashboard.monthlyExpenses.cumulativeExpenses")}
            </Body>
            {chartData.length > 0 ? (
              <div className="h-32 -mx-2 relative flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <Tooltip
                      formatter={(value: any) => {
                        if (value === null || value === undefined) return "N/A"
                        if (typeof value === "number") {
                          return <PrivacyBlur>{formatCurrency(value)}</PrivacyBlur>
                        }
                        return String(value)
                      }}
                      labelFormatter={(label) =>
                        t("components.dashboard.monthlyExpenses.day", {
                          day: label,
                        })
                      }
                      contentStyle={{
                        backgroundColor: themeColors.card,
                        border: `1px solid ${themeColors.border}`,
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: themeColors.cardForeground,
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      wrapperStyle={{
                        outline: "none",
                      }}
                      itemStyle={{
                        color: themeColors.cardForeground,
                      }}
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 10 }}
                      tickCount={5}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: "12px",
                        paddingTop: "8px",
                      }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="current"
                      name={
                        currentMonthDaily?.monthLabel ||
                        t("components.dashboard.monthlyExpenses.currentMonth")
                      }
                      stroke={currentColor}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                    />
                    {previousMonthDaily && (
                      <Line
                        type="monotone"
                        dataKey="previous"
                        name={previousMonthDaily.monthLabel}
                        stroke={previousColor}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        strokeDasharray="3 3"
                        connectNulls={true}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-32 bg-muted/10 rounded-md flex items-center justify-center border border-border/50">
                <Body className="text-muted-foreground text-sm">
                  {t("components.dashboard.monthlyExpenses.noData")}
                </Body>
              </div>
            )}
            <Body className="text-xs text-muted-foreground mt-1.5">
              {t("components.dashboard.monthlyExpenses.runningTotal")}
            </Body>
          </div>

          {/* Tiny Line Chart for Expenses - Daily */}
          <div className="mb-4">
            <Body className="text-sm font-medium text-foreground mb-1.5">
              {t("components.dashboard.monthlyExpenses.dailyExpenses")}
            </Body>
            {dailyChartData.length > 0 ? (
              <div className="h-32 -mx-2 relative flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <LineChart
                    data={dailyChartData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <Tooltip
                      formatter={(value: any) => {
                        if (value === null || value === undefined) return "N/A"
                        if (typeof value === "number") {
                          return <PrivacyBlur>{formatCurrency(value)}</PrivacyBlur>
                        }
                        return String(value)
                      }}
                      labelFormatter={(label) =>
                        t("components.dashboard.monthlyExpenses.day", {
                          day: label,
                        })
                      }
                      contentStyle={{
                        backgroundColor: themeColors.card,
                        border: `1px solid ${themeColors.border}`,
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: themeColors.cardForeground,
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      wrapperStyle={{
                        outline: "none",
                      }}
                      itemStyle={{
                        color: themeColors.cardForeground,
                      }}
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 10 }}
                      tickCount={5}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: "12px",
                        paddingTop: "8px",
                      }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="current"
                      name={
                        currentMonthDaily?.monthLabel ||
                        t("components.dashboard.monthlyExpenses.currentMonth")
                      }
                      stroke={currentColor}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                    />
                    {previousMonthDaily && (
                      <Line
                        type="monotone"
                        dataKey="previous"
                        name={previousMonthDaily.monthLabel}
                        stroke={previousColor}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        strokeDasharray="3 3"
                        connectNulls={true}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-32 bg-muted/10 rounded-md flex items-center justify-center border border-border/50">
                <Body className="text-muted-foreground text-sm">
                  {t("components.dashboard.monthlyExpenses.noData")}
                </Body>
              </div>
            )}
            <Body className="text-xs text-muted-foreground mt-1.5">
              {t("components.dashboard.monthlyExpenses.dailyAmount")}
            </Body>
          </div>

          {/* Monthly Summary */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50 flex-shrink-0">
            <div>
              <Body className="text-xs text-muted-foreground mb-1.5">
                {currentMonthDaily?.monthLabel ||
                  t("components.dashboard.monthlyExpenses.currentMonth")}
              </Body>
              <MetricValue>{formatCurrency(totalExpenses)}</MetricValue>
            </div>
            {previousMonthTotals && (
              <div>
                <Body className="text-xs text-muted-foreground mb-1.5">
                  {previousMonthTotals.monthLabel}
                </Body>
                <MetricValue className="text-muted-foreground">
                  {formatCurrency(previousExpenses)}
                </MetricValue>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

MonthlyExpensesCard.displayName = "MonthlyExpensesCard"
